/* eslint-disable max-len */
import { i18n } from '@goodboydigital/astro';
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Signal } from 'typed-signals';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { AnalyticsEvent } from '../../plugins/AnalyticsPlugin';
import { Maths } from '../../utils/Maths';
import { Random } from '../../utils/Random';
import { StrUtils } from '../../utils/StrUtils';
import { VehicleData, VehicleEntity } from '../entities/VehicleEntity';
import { Game } from '../Game';

export type RaceType = 'standard' | 'nemesis' | 'tutorial';
export type RaceDifficulty = 'easy' | 'normal' | 'hard';
export enum RaceState
    {
    IDLE,
    COUNTDOWN,
    RUNNING,
    FINISHED,
}

enum BoostState
    {
    SLOW = -1,
    NORMAL = 0,
    FAST = 1,
}

/** How many players (excluding user's) should be in the race  */
const MAX_OPPONENTS = 5;

/** Nemesis race interval - if 5, 5th, 10th, 15th will be nemesis race  */
const NEMESIS_RACE_INTERVAL = 5;

const NEMESIS_RACE_BRANDS = ["awog", "ttg", "craigofthecreek", "wbb", "appleonion"];

/** Number of races for each difficulty level  */
const RACES_PER_DIFFICULTY = 10;

export class RaceSystem implements SystemInterface
{
    public game: Game;
    public players = 0;
    public position = 0;
    public courseLength = 0;
    public coursePosition = 0;
    public courseRatio = 0;
    public time = 0;
    public analyticsTime = 0;
    public collectedCoins = 0;
    public bonusCoins = 0;
    public step = 0;
    public state = RaceState.IDLE;
    public finishTime = 0;
    public bestRunBeaten = false;
    public raceIndex = 0;
    public availableCharacters?: string[];
    public ghosts: VehicleEntity[];

    // Race perks for the main player
    public racePerkFlipSpeed: number;
    public racePerkMaxSpeed: number;
    public racePerkTrickBonus: number;

    /** Fire when race begins */
    public onRaceCountdownStart = new Signal<() => void>();

    /** Fire when race begins */
    public onRaceStart = new Signal<() => void>();

    /** Fire when player finish the race */
    public onRaceFinish = new Signal<() => void>();

    private _boostAmount: BoostState = BoostState.SLOW;
    private _intervalState = -1;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.game.physics.physicsFixedUpdateRunner.add(this as any);
        this.game.physics.physicsFreeUpdateRunner.add(this as any);
        this.game.onIdle.connect(() => this.onGameIdle());
        this.ghosts = [];
        this.racePerkMaxSpeed = 0;
        this.racePerkFlipSpeed = 0;
        this.racePerkTrickBonus = 0;
    }

    /**
     * Get max number of opponents allowed in a race
     * @returns
     */
    public getCurrentRaceMaxOpponents(): number
    {
        return this.getCurrentRaceType() === 'standard' ? MAX_OPPONENTS : 1;
    }

    /**
     * Get race type by race index - every 5th race it will be 'nemesis', otherwise is 'standard'
     * @returns
     */
    public getRaceTypeByIndex(index: number): RaceType
    {
        if (index === 0) return 'tutorial';

        return index % NEMESIS_RACE_INTERVAL ? 'standard' : 'nemesis';
    }

    public getNemesisCountByIndex(index: number): number
    {
        return Math.floor(index / NEMESIS_RACE_INTERVAL);
    }

    public getNemesisBrand(raceIndex: number): string
    {
        return NEMESIS_RACE_BRANDS[(Math.floor(this.getNemesisCountByIndex(raceIndex) / NEMESIS_RACE_INTERVAL)) % NEMESIS_RACE_BRANDS.length];
    }

    /**
     * Get current race type - 'standard' or 'nemesis'
     * @returns
     */
    public getCurrentRaceType(): RaceType
    {
        return this.getRaceTypeByIndex(this.raceIndex);
    }

    /**
     * Return current race difficulty based on current index
     * @returns
     */
    public getCurrentRaceDifficulty(): RaceDifficulty
    {
        if (this.raceIndex <= RACES_PER_DIFFICULTY)
        {
            return 'easy';
        }
        else if (this.raceIndex <= RACES_PER_DIFFICULTY * 2)
        {
            return 'normal';
        }

        return 'hard';
    }

    /**
     * Get the tier level for the current race, used mostly for gear randomisation
     * while recording
     * @returns
     */
    public getCurrentRaceGearTier(): number
    {
        if (this.raceIndex <= 5) return 0;
        if (this.raceIndex <= 10) return 1;
        if (this.raceIndex <= 15) return 2;

        return 3;
    }

    /** Pick a single character id to be the nemesis */
    public getNemesisOpponent(): string
    {
        if (this.getCurrentRaceType() !== 'nemesis') return '';

        if (this.ghosts.length) return this.ghosts[0].character.data?.id ?? '';

        // Refresh available characters list if not defined
        if (!this.availableCharacters)
        {
            this.availableCharacters = this.getAvailableCharacters(Shared.user.data.vehicle.character, this.getNemesisBrand(this.raceIndex));
        }
        return this.availableCharacters[0] ?? '';
    }

    /** Prepare a race by index */
    public setupRace(raceIndex: number): void
    {
        this.clearGhosts();
        this.raceIndex = raceIndex;
        this.racePerkMaxSpeed = 0;
        this.racePerkFlipSpeed = 0;
        this.racePerkTrickBonus = 0;
    }

    /** Retrieve a list of available characters for this race, usually exlucing main player's character */
    private getAvailableCharacters(excluding: string, brand?: string): string[]
    {
        var available = Random.shuffle(this.game.app.data.getAvailableCharacters().slice(0));
        const excludingIndex = available.indexOf(excluding);

        if (excludingIndex >= 0) available.splice(excludingIndex, 1);

        if(brand){
            available = available.filter((value: string, index: number, array: string[]) => {
                var characterData = this.game.app.data.getCharacterData(value);
                return characterData.brand == brand;
            });
        }

        return available;
    }

    /**
     * Validates and add ghost data to a race. There are some reasons that given data might be invalidated and return null:
     * - If there are no available characters left for the race
     * - If vehicle parts from data are not available anymore
     * - If race already reached max number of players
     * Level hash verification should be done prior to add the ghost data - ReplaySystem is doing that at the moment
     * @param data - VehicleData to be added to the race
     * @returns A vehicle entity or null, if data was rejected
     */
    public addGhost(data: VehicleData): VehicleEntity | null
    {
        // Refresh available characters list if not defined
        if (!this.availableCharacters)
        {
            this.availableCharacters = this.getAvailableCharacters(Shared.user.data.vehicle.character);
        }

        // Check if there are any characters available
        if (!this.availableCharacters.length || !this.game.playing)
        {
            console.log('[RaceSystem] No characters left - opponent data ignored:', data);

            return null;
        }

        // Check if data provided is valid
        if (!this.game.app.data.validateVehicle(data))
        {
            console.log('[RaceSystem] Opponent data invalidated:', data);

            return null;
        }

        // Ignore if there are already enough players in the race
        if (this.ghosts.length >= this.getCurrentRaceMaxOpponents())
        {
            console.log('[RaceSystem] Max opponents reached, opponent data ignored:', data);

            return null;
        }

        console.log('[RaceSystem] Opponent added:', data);
        console.log('[RaceSystem] Available characters as opponents:', this.availableCharacters.join(','));

        // Randomise parts that does not influence on physics
        data.nickname = StrUtils.randomNickname();
        data.flag = this.game.app.data.getRandomFlag();
        data.colorWheelLeft = this.game.app.data.getRandomColor();
        data.colorWheelRight = this.game.app.data.getRandomColor();
        data.colorChassis = this.game.app.data.getRandomColor();
        data.extra = this.game.app.data.getRandomDeco();
        data.trailLeft = this.game.app.data.getRandomTrail();
        data.trailRight = data.trailLeft;
        
        // Replace character by one of the available
        data.character = this.availableCharacters.shift() as string;

        // Normalise data just to ensure all fields are valid
        data = this.game.garage.normaliseVehicleData(data);

        // Set up the ghostvehicle, and return null if somehow that goes wrong
        const vehicle = this.game.pool.get(VehicleEntity) as VehicleEntity;

        try
        {
            // Try to build the vehicle with given data, which comes from API
            vehicle.build(data, true);
        }
        catch (e)
        {
            console.log('Invalid vehicle data:', data);
            this.game.pool.return(vehicle);

            return null;
        }

        // Setup ghost vehicle
        vehicle.updateColor(data.colorChassis, data.colorWheelLeft, data.colorWheelRight);
        vehicle.character.spine.playIdle();
        vehicle.decoBack.physicsFreeUpdate();
        vehicle.decoFront.physicsFreeUpdate();
        if(vehicle.tag){
            vehicle.tag.turnOff();
        }
        
        vehicle.selfAddToGame();

        // Add ghost vehicle to the game and update depth order
        this.ghosts.push(vehicle);
        this.updateVehiclesOrder();

        return vehicle;
    }

    /** Remove all ghosts from game and reset available characters */
    public clearGhosts(): void
    {
        for (const ghost of this.ghosts)
        {
            ghost.selfRemoveFromGame();
            this.game.pool.return(ghost);
        }
        this.ghosts = [];
        this.availableCharacters = undefined;
    }

    /** Ensure the sprite order of all vehicles and always put main player in front */
    public updateVehiclesOrder(): void
    {
        for (let i = 0; i < this.ghosts.length; i++) this.ghosts[i].setDepth(i);
        this.game.vehicle?.setDepth(this.ghosts.length);
    }

    /** Get a readable name for the race. Ex.: "#6 - STANDARD RACE" */
    public getRaceName(): string
    {
        const raceType = this.getCurrentRaceType();
        const raceName = i18n.translate(`game_raceName_${raceType}`);

        if (raceType === 'tutorial') return raceName;

        return `#${this.raceIndex} - ${raceName}`;
    }

    /** Fire when game become idle */
    private onGameIdle(): void
    {
        this.state = RaceState.IDLE;
        this.clearGhosts();
    }

    /** Start the race countdown, with the trafic lights */
    public countdownRace(): void
    {
        if (this.state !== RaceState.IDLE) return;
        this.state = RaceState.COUNTDOWN;
        this.game.vehicle.onYourMark();
        this.game.level.limitMovementToStartingPoint();
        this.onRaceCountdownStart.emit();

        // Brute force countdown - 0.6 on each step instead of 1 second
        const baseDelay = this.raceIndex === 0 || Settings.fastplay ? 0 : 1;
        const interval = 0.4;

        this.game.hud.messageWidget.close();
        this.game.hud.trafficLightsWidget.open();
        if (this.raceIndex > 0) this.game.hud.opponentLoader.open();

        this.game.physics.timer.dispatchAfter(baseDelay + (interval), () =>
        {
            this._intervalState = 4;
            this.game.hud.opponentLoader.close();
            this.game.hud.trafficLightsWidget.setState(2);
            Shared.game.soundSystem.playSfx('countdown');
        });
        this.game.physics.timer.dispatchAfter(baseDelay + (interval * 2), () =>
        {
            this._intervalState = 3;
            this.game.hud.trafficLightsWidget.setState(3);
            Shared.game.soundSystem.playSfx('countdown');
        });
        this.game.physics.timer.dispatchAfter(baseDelay + (interval * 3), () =>
        {
            this._intervalState = 2;
            this.game.hud.trafficLightsWidget.setState(4);
            Shared.game.soundSystem.playSfx('countdown');
        });
        this.game.physics.timer.dispatchAfter(baseDelay + (interval * 4), () =>
        {
            this._intervalState = 1;
            this.game.hud.trafficLightsWidget.setState(5);
            Shared.game.soundSystem.playSfx('countdown-go');
            this.startRace();
        });
    }

    public checkBoost(value: boolean): void
    {
        // if the current interval is 2 we allow the boost if value === true
        // if value === false and its not interval of 2 we remove the boost
        if (value && this._intervalState === 2)
        {
            this._boostAmount = BoostState.FAST;
        }
        else if (value && this._intervalState === 4)
        {
            this._boostAmount = BoostState.SLOW;
        }
        else
        {
            this._boostAmount = BoostState.NORMAL;
        }
    }

    public startRace(): void
    {
        if (this.state === RaceState.RUNNING) return;
        this.state = RaceState.RUNNING;
        this.game.physics.reset();
        this.game.level.levelReset();
        this.players = 0;
        this.position = 0;
        this.courseLength = 0;
        this.coursePosition = 0;
        this.courseRatio = 0;
        this.time = 0;
        this.analyticsTime = 0;
        this.collectedCoins = 0;
        this.bonusCoins = 0;
        this.step = 0;
        this.finishTime = 0;
        this.bestRunBeaten = false;
        this.game.vehicle.startRace();
        this.onRaceStart.emit();
        
        if(this.raceIndex == 0)
        {
            this.game.app.analytics.trackEvent(AnalyticsEvent.TUTORIAL_PLAY);
        }
        else
        {
            this.game.app.analytics.trackEvent(AnalyticsEvent.GAME_PLAY);
        }
        
        console.log(`[RaceSystem] player boost value: ${this._boostAmount}`);
        this.game.vehicle.motor.boost(this._boostAmount);
        if (this._boostAmount >= BoostState.FAST) this.game.vehicle.playBoostEffect();
    }

    public finishRace(): void
    {
        if (this.state !== RaceState.RUNNING) return;
        this.state = RaceState.FINISHED;
        
        this.finishTime = Math.round((this.time + Number.EPSILON) * 100) / 100;

        const levelHash = this.game.level.getLevelHash();
        const user = this.game.app.user;
        const bestTime = user.data.bestRuns[levelHash] || -1;

        // Check if finish time is better than previous finish time for this level
        if (bestTime <= 0 || this.finishTime < bestTime)
        {
            this.bestRunBeaten = true;
            user.data.bestRuns[levelHash] = this.finishTime;
        }

        user.setRaceResults(this.raceIndex, this.position, this.players, this.finishTime);
        user.changeCoins(this.coins);
        user.nextRace();
        user.save();
        
        if(Shared.user.numRacesPlayed() >= Settings.maxRaces){
            this.game.app.analytics.trackEvent(AnalyticsEvent.GAME_COMPLETE);
        }
        
        this.onRaceFinish.emit();
        
    }

    public physicsFixedUpdate(): void
    {
        if (!this.game.playing) return;

        const vehicle = this.game.vehicle;

        if (this.state === RaceState.IDLE)
        {
            // Check if vehicle has reached the starting flag
            if (vehicle.assembled && vehicle.chassis.physics.x > this.game.level.courseStartX)
            {
                // Vehicle should reach start flag straight, otherwise would be a risk of crash during the countdown
                if (vehicle.isStraight())
                {
                    // Vehicle reached start flag in a safe angle
                    this.countdownRace();
                }
                else
                {
                    // Vehicle reached start flag in a dangerouns angle
                    vehicle.crash();
                    
                    this.game.hud.goWidget.close();
                    this.game.hud.messageWidget.showWheelieWarning();
                    this.game.checkpoint.resetCheckpoint({ x: this.game.level.courseStartX - 500, y: this.game.level.courseStartY - 100 });
                }
            }
        }
        else if (this.state === RaceState.RUNNING)
        {
            this.time += 16.6666;
            this.step += 1;
        }
        
        if(this.analyticsTime < 30){
            this.analyticsTime += 0.0166666;
            
            if(this.analyticsTime >= 30){
                this.game.app.analytics.trackEvent(AnalyticsEvent.GAME_TIME_SPENT_30_SECS);
            }
        }
    }

    public physicsFreeUpdate(): void
    {
        if (!this.game.playing) return;
        
        const vehicle = this.game.vehicle;

        if (this.state === RaceState.RUNNING)
        {
            // Only compute course position if vehicle is in one piece
            if (this.game.vehicle.assembled)
            {
                this.coursePosition = vehicle.character.physics.x - this.game.level.courseStartX;
            }

            this.courseLength = this.game.level.courseEndX - this.game.level.courseStartX;
            this.courseRatio = Maths.clamp(this.coursePosition / this.courseLength);
            this.players = this.ghosts.length + 1;
            this.position = 1;

            let i = this.ghosts.length;

            while (i--)
            {
                const ghostCourseRatio = this.getGhostCourseRatio(i);

                if (ghostCourseRatio > this.courseRatio) this.position += 1;
            }
        }
    }

    public getGhostCourseRatio(ghostIndex: number): number
    {
        const ghost = this.ghosts[ghostIndex];
        const ghostCoursePosition = ghost.character.physics.x - this.game.level.courseStartX;

        return Maths.clamp(ghostCoursePosition / this.courseLength);
    }

    public get running(): boolean
    {
        return this.state === RaceState.RUNNING;
    }

    public get idle(): boolean
    {
        return this.state === RaceState.IDLE;
    }

    public get coins(): number
    {
        return this.collectedCoins + this.bonusCoins;
    }

    public getProfile(): Record<string, any>
    {
        return {
            raceIndex: this.raceIndex,
            position: this.position,
            players: this.players,
            courseRatio: this.courseRatio,
            courseLength: this.courseLength,
            difficulty: this.getCurrentRaceDifficulty(),
        };
    }

    public update(): void
    {
        //
    }
}
